"use client";

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import type { TopicVocabulary, WordEntry, ExpressionEntry, BuildingBlock } from "@/lib/dialog-data";
import { useCollapsibleSet } from "@/hooks/use-collapsible-set";
import { useVocabularySearch } from "@/hooks/use-vocabulary-search";
import { ChevronDown, ChevronUp, FolderOpen, BookOpen, MessageSquareText, ListTree, Search, X, Puzzle, EyeOff, Bookmark, Loader2, Star, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTTS } from "@/lib/use-tts";
import { useLanguage } from "@/lib/language-context";
import { useTranslations } from "@/lib/use-translations";
import { SpeakerButton } from "./speaker-button";
import { useWordState } from "@/lib/use-word-state";

const BUILDING_BLOCKS_HIDDEN_KEY = "saysay-building-blocks-hidden";
const BOOKMARKED_WORDS_KEY = "saysay-bookmarked-words";

/**
 * Normalize word display: lowercase unless it's an acronym (all caps) or proper noun
 */
function normalizeWordDisplay(word: string, partOfSpeech?: string): string {
  if (!word) return word;

  // Keep acronyms as-is (all uppercase, e.g., "USA", "NATO")
  if (word === word.toUpperCase() && word.length > 1) {
    return word;
  }

  // Keep proper nouns capitalized
  if (partOfSpeech?.toLowerCase().includes("proper") ||
      partOfSpeech?.toLowerCase().includes("name")) {
    return word;
  }

  // Lowercase everything else
  return word.toLowerCase();
}

interface VocabularySectionProps {
  vocabulary: TopicVocabulary;
  isStreaming?: boolean;
}

type VocabTab = "words" | "expressions" | "topical";

export function VocabularySection({ vocabulary, isStreaming }: VocabularySectionProps) {
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [bookmarkedWords, setBookmarkedWords] = useState<Set<string>>(new Set());
  const { isCollapsed, toggle: toggleCategory } = useCollapsibleSet();
  const [activeTab, setActiveTab] = useState<VocabTab>("words");
  const { query: searchQuery, setQuery: setSearchQuery, filtered: filteredVocabulary, filteredCount } = useVocabularySearch(vocabulary);
  const { targetLanguage } = useLanguage();
  const t = useTranslations();
  const wordState = useWordState();

  // Building blocks visibility - collapsed by default if user marked "I already know this"
  const [buildingBlocksHidden, setBuildingBlocksHidden] = useState(true);
  const [buildingBlocksExpanded, setBuildingBlocksExpanded] = useState(false);

  // Load bookmarked words from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(BOOKMARKED_WORDS_KEY);
    if (saved) {
      try {
        setBookmarkedWords(new Set(JSON.parse(saved)));
      } catch {
        // Invalid JSON, reset
      }
    }
  }, []);

  // Load building blocks preference from localStorage
  useEffect(() => {
    const hidden = localStorage.getItem(BUILDING_BLOCKS_HIDDEN_KEY) === "true";
    setBuildingBlocksHidden(hidden);
  }, []);

  // Mark building blocks as known (hide by default)
  const markBuildingBlocksAsKnown = () => {
    localStorage.setItem(BUILDING_BLOCKS_HIDDEN_KEY, "true");
    setBuildingBlocksHidden(true);
    setBuildingBlocksExpanded(false);
  };

  // Show building blocks again
  const showBuildingBlocks = () => {
    localStorage.setItem(BUILDING_BLOCKS_HIDDEN_KEY, "false");
    setBuildingBlocksHidden(false);
  };

  const toggleBookmark = useCallback((wordKey: string) => {
    setBookmarkedWords((prev) => {
      const next = new Set(prev);
      if (next.has(wordKey)) {
        next.delete(wordKey);
      } else {
        next.add(wordKey);
      }
      // Save to localStorage
      localStorage.setItem(BOOKMARKED_WORDS_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  const [speakingText, setSpeakingText] = useState<string | null>(null);
  const { speak, stop, state: ttsState } = useTTS({ lang: targetLanguage.code });

  // Clear speaking text when TTS stops
  useEffect(() => {
    if (ttsState === "idle" && speakingText) {
      setSpeakingText(null);
    }
  }, [ttsState, speakingText]);

  const handleSpeak = useCallback((text: string) => {
    if (ttsState === "playing" && speakingText === text) {
      stop();
    } else {
      setSpeakingText(text);
      speak(text);
    }
  }, [ttsState, speakingText, speak, stop]);

  const getStateForText = useCallback((text: string) =>
    speakingText === text ? ttsState : "idle", [speakingText, ttsState]);

  // Render a word entry card (from dialog with linked forms)
  // Also used for building blocks since they now have the same structure
  const renderWordEntry = (entry: WordEntry | BuildingBlock, index: number, keyPrefix = "word") => {
    const wordKey = `${keyPrefix}-${entry.baseForm}-${index}`;
    // Word is expanded if it's selected OR if one of its forms is selected
    const isExpanded = expandedItem === wordKey || expandedItem?.startsWith(`${keyPrefix}-form-${entry.baseForm}-`);
    // Check if this is a building block (has partOfSpeech)
    const partOfSpeech = "partOfSpeech" in entry ? entry.partOfSpeech : undefined;
    const isBookmarked = bookmarkedWords.has(wordKey);

    return (
      <Card
        key={wordKey}
        className={cn(
          "transition-all overflow-hidden",
          isBookmarked && "border-primary/40"
        )}
      >
        <CardContent className="p-0">
          <button
            type="button"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
            onClick={() => setExpandedItem(isExpanded ? null : wordKey)}
          >
            <SpeakerButton
              state={getStateForText(entry.baseForm)}
              onClick={(e) => {
                e.stopPropagation();
                handleSpeak(entry.baseForm);
              }}
              size="sm"
              className="shrink-0"
            />

            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-foreground">{normalizeWordDisplay(entry.baseForm, partOfSpeech)}</p>
              <p className="text-sm text-muted-foreground">{entry.translation}</p>
              {/* Show linked forms preview */}
              {entry.forms && entry.forms.length > 1 && (
                <p className="text-[11px] text-primary/70 mt-0.5">
                  {t.learning.forms}: {entry.forms.slice(0, 3).map(f => normalizeWordDisplay(f.form, partOfSpeech)).join(", ")}{entry.forms.length > 3 ? "..." : ""}
                </p>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {/* Part of speech badge (for building blocks) */}
              {partOfSpeech && (
                <Badge variant="outline" className="text-[11px]">
                  {partOfSpeech}
                </Badge>
              )}
              {/* Bookmark button */}
              <Button
                variant="ghost"
                size="icon"
                className="w-7 h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBookmark(wordKey);
                }}
              >
                <Bookmark className={cn("w-3.5 h-3.5", isBookmarked && "fill-primary text-primary")} />
              </Button>
              {isExpanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </div>
          </button>

          {isExpanded && (
            <div className="px-3 pb-3 pt-0 border-t border-border">
              <div className="pt-2 space-y-3">
                {/* Word state toggles */}
                <div className="flex gap-2 pb-2 border-b border-border/40">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); wordState.toggleKnown(entry.baseForm); }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] transition-all border ${
                      wordState.isKnown(entry.baseForm)
                        ? "bg-slate-500/20 border-slate-500/40 text-slate-600 dark:text-slate-400"
                        : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <EyeOff className="w-3 h-3" />
                    <span>{t.learning.markKnown || "I know this"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); wordState.toggleTough(entry.baseForm); }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] transition-all border ${
                      wordState.isTough(entry.baseForm)
                        ? "bg-amber-500/20 border-amber-500/40 text-amber-600 dark:text-amber-400"
                        : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <Star className="w-3 h-3" />
                    <span>{t.learning.markTough || "Tough one"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); wordState.toggleLiked(entry.baseForm); }}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] transition-all border ${
                      wordState.isLiked(entry.baseForm)
                        ? "bg-rose-500/20 border-rose-500/40 text-rose-600 dark:text-rose-400"
                        : "bg-muted/50 border-border/50 text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    <Heart className="w-3 h-3" />
                    <span>{t.learning.markLiked || "Like"}</span>
                  </button>
                </div>

                {entry.pronunciation && (
                  <div>
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-0.5">{t.learning.pronunciation}</p>
                    <p className="text-sm font-mono text-foreground">/{entry.pronunciation}/</p>
                  </div>
                )}

                {entry.vocalizationTip && (
                  <div>
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-0.5">{t.learning.sayItLike || "Say it like"}</p>
                    <p className="text-sm text-primary font-medium">{entry.vocalizationTip}</p>
                  </div>
                )}

                {/* All linked forms with usage, examples, and context */}
                {entry.forms && entry.forms.length > 0 && (
                  <div className="pt-1 border-t border-border/40">
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-1.5">{t.learning.allForms}</p>
                    <div className="space-y-1.5">
                      {entry.forms.map((formEntry) => {
                        const formKey = `${keyPrefix}-form-${entry.baseForm}-${formEntry.form}`;
                        const isFormExpanded = expandedItem === formKey;

                        return (
                          <div
                            key={formEntry.form}
                            className="rounded-md bg-muted/30 overflow-hidden"
                          >
                            {/* Form header - always visible */}
                            <button
                              type="button"
                              className="w-full flex items-center gap-1.5 p-1.5 hover:bg-muted/50 text-left"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedItem(isFormExpanded ? wordKey : formKey);
                              }}
                            >
                              <Badge variant="outline" className="text-[11px] font-semibold shrink-0">
                                {normalizeWordDisplay(formEntry.form, partOfSpeech)}
                              </Badge>
                              <span className="text-[11px] text-muted-foreground flex-1 truncate">
                                {formEntry.usage}
                                {formEntry.vocalizationTip && (
                                  <span className="text-primary ml-1">[{formEntry.vocalizationTip}]</span>
                                )}
                              </span>
                              <SpeakerButton
                                state={getStateForText(formEntry.form)}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSpeak(formEntry.form);
                                }}
                                size="sm"
                                className="w-5 h-5 shrink-0"
                                iconClassName="w-2.5 h-2.5"
                              />
                              {(formEntry.example || formEntry.context) && (
                                isFormExpanded ? (
                                  <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                                )
                              )}
                            </button>

                            {/* Expanded form details */}
                            {isFormExpanded && (formEntry.example || formEntry.context) && (
                              <div className="px-2 pb-2 pt-1 border-t border-border/50 space-y-2">
                                {/* Example sentence */}
                                {formEntry.example && (
                                  <div>
                                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-0.5">{t.learning.example}</p>
                                    <div className="flex items-start gap-1.5">
                                      <div className="flex-1">
                                        <p className="text-sm text-foreground">{formEntry.example.sentence}</p>
                                        <p className="text-[11px] text-primary mt-0.5">{formEntry.example.translation}</p>
                                      </div>
                                      <SpeakerButton
                                        state={getStateForText(formEntry.example!.sentence)}
                                        onClick={() => handleSpeak(formEntry.example!.sentence)}
                                        size="sm"
                                        className="w-5 h-5 shrink-0"
                                        iconClassName="w-2.5 h-2.5"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Context - conversation flow */}
                                {formEntry.context && (
                                  <div>
                                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-0.5">{t.learning.inContext}</p>
                                    <div className="bg-background/50 rounded-md p-1.5 space-y-1">
                                      {formEntry.context.before && (
                                        <p className="text-[11px] text-muted-foreground italic">"{formEntry.context.before}"</p>
                                      )}
                                      <div className="flex items-start gap-1.5 bg-primary/10 -mx-1.5 px-1.5 py-1 rounded">
                                        <p className="text-sm text-foreground font-medium flex-1">"{formEntry.context.line}"</p>
                                        <SpeakerButton
                                          state={getStateForText(formEntry.context!.line)}
                                          onClick={() => handleSpeak(formEntry.context!.line)}
                                          size="sm"
                                          className="w-4 h-4 shrink-0"
                                          iconClassName="w-2 h-2"
                                        />
                                      </div>
                                      {formEntry.context.after && (
                                        <p className="text-[11px] text-muted-foreground italic">"{formEntry.context.after}"</p>
                                      )}
                                      <p className="text-[11px] text-primary/70 pt-1 border-t border-border/30">
                                        {formEntry.context.situation}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Examples from dialog with translations */}
                {entry.examples && entry.examples.length > 0 && (
                  <div className="pt-1 border-t border-border/40">
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-1.5">{t.learning.fromDialog}</p>
                    <div className="space-y-2">
                      {entry.examples.map((example, i) => (
                        <div key={i} className="border-l-2 border-primary/30 pl-2">
                          <div className="flex items-start gap-1.5">
                            <p className="text-sm text-foreground flex-1">{example.text}</p>
                            <SpeakerButton
                              state={getStateForText(example.text)}
                              onClick={() => handleSpeak(example.text)}
                              size="sm"
                              className="w-5 h-5 shrink-0"
                              iconClassName="w-2.5 h-2.5"
                            />
                          </div>
                          <p className="text-[11px] text-primary mt-0.5">{example.translation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render an expression entry card
  const renderExpressionEntry = (entry: ExpressionEntry, _index: number) => {
    const wordKey = `expr-${entry.expression}`;
    const isExpanded = expandedItem === wordKey;
    const isBookmarked = bookmarkedWords.has(wordKey);

    return (
      <Card
        key={wordKey}
        className={cn(
          "transition-all overflow-hidden",
          isBookmarked && "border-primary/40"
        )}
      >
        <CardContent className="p-0">
          <button
            type="button"
            className="w-full px-3 py-2.5 flex items-center gap-2 text-left"
            onClick={() => setExpandedItem(isExpanded ? null : wordKey)}
          >
            <SpeakerButton
              state={getStateForText(entry.expression)}
              onClick={(e) => {
                e.stopPropagation();
                handleSpeak(entry.expression);
              }}
              size="sm"
              className="shrink-0"
            />

            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-foreground">{normalizeWordDisplay(entry.expression)}</p>
              <p className="text-sm text-muted-foreground">{entry.translation}</p>
              {entry.vocalizationTip && (
                <p className="text-sm text-primary">[{entry.vocalizationTip}]</p>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="w-7 h-7"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBookmark(wordKey);
                }}
              >
                <Bookmark className={cn("w-3.5 h-3.5", isBookmarked && "fill-primary text-primary")} />
              </Button>
              {isExpanded ? (
                <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              )}
            </div>
          </button>

          {isExpanded && (
            <div className="px-3 pb-3 pt-0 border-t border-border">
              <div className="pt-2 space-y-3">
                {entry.literal && (
                  <div>
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-0.5">{t.learning.literalMeaning}</p>
                    <p className="text-sm text-foreground/70 italic">"{entry.literal}"</p>
                  </div>
                )}

                {/* Word breakdown */}
                {entry.components && entry.components.length > 0 && (
                  <div className="pt-1 border-t border-border/40">
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-1.5">{t.learning.wordBreakdown}</p>
                    <div className="space-y-1">
                      {entry.components.map((comp, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-1.5 p-1.5 rounded-md bg-muted/30 hover:bg-muted/50 cursor-pointer"
                          onClick={() => handleSpeak(comp.word)}
                        >
                          <Badge variant="outline" className="text-[11px] font-semibold shrink-0">
                            {normalizeWordDisplay(comp.word)}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <span className="text-[11px] text-foreground">{comp.meaning}</span>
                            {comp.note && (
                              <span className="text-[11px] text-muted-foreground ml-1">· {comp.note}</span>
                            )}
                          </div>
                          <SpeakerButton
                            state={getStateForText(comp.word)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSpeak(comp.word);
                            }}
                            size="sm"
                            className="w-4 h-4 shrink-0"
                            iconClassName="w-2.5 h-2.5"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {entry.examples && entry.examples.length > 0 && (
                  <div className="pt-1 border-t border-border/40">
                    <p className="text-[11px] text-muted-foreground/80 uppercase tracking-wider font-semibold mb-1.5">{t.learning.fromDialog}</p>
                    <div className="space-y-2">
                      {entry.examples.map((example, i) => (
                        <div key={i} className="border-l-2 border-primary/30 pl-2">
                          <div className="flex items-start gap-1.5">
                            <p className="text-sm text-foreground flex-1">{example.text}</p>
                            <SpeakerButton
                              state={getStateForText(example.text)}
                              onClick={() => handleSpeak(example.text)}
                              size="sm"
                              className="w-5 h-5 shrink-0"
                              iconClassName="w-2.5 h-2.5"
                            />
                          </div>
                          <p className="text-[11px] text-primary mt-0.5">{example.translation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render topical vocabulary categories
  const renderTopicalSection = () => {
    return (
      <div className="flex flex-col gap-4">
        {filteredVocabulary.topical.map((category) => {
          const categoryCollapsed = isCollapsed(category.category);
          const categoryBookmarked = category.items.filter((item) =>
            bookmarkedWords.has(`topical-${category.category}-${item.word}`)
          ).length;

          return (
            <div key={category.category}>
              <button
                type="button"
                onClick={() => toggleCategory(category.category)}
                className="w-full flex items-center gap-2 mb-1.5 text-left"
              >
                <FolderOpen className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold text-foreground flex-1">{category.category}</span>
                {categoryBookmarked > 0 && (
                  <Badge variant="secondary" className="text-[11px]">
                    <Bookmark className="w-2.5 h-2.5 mr-0.5 fill-current" />{categoryBookmarked}
                  </Badge>
                )}
                <span className="text-[11px] text-muted-foreground">{category.items.length}</span>
                {categoryCollapsed ? (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </button>

              {!categoryCollapsed && (
                <div className="flex flex-col gap-1.5 pl-1">
                  {category.items.map((item) => {
                    const wordKey = `topical-${category.category}-${item.word}`;
                    const isBookmarked = bookmarkedWords.has(wordKey);

                    return (
                      <Card
                        key={wordKey}
                        className={cn(
                          "transition-all overflow-hidden",
                          isBookmarked && "border-primary/40"
                        )}
                      >
                        <CardContent className="p-0">
                          <div className="px-3 py-2 flex items-center gap-2">
                            <SpeakerButton
                              state={getStateForText(item.word)}
                              onClick={() => handleSpeak(item.word)}
                              size="sm"
                              className="shrink-0"
                            />

                            <div className="flex-1 min-w-0">
                              <p className="text-base font-semibold text-foreground">{normalizeWordDisplay(item.word)}</p>
                              <p className="text-sm text-muted-foreground">{item.translation}</p>
                              {item.pronunciation && (
                                <p className="text-[11px] text-muted-foreground">/{item.pronunciation}/</p>
                              )}
                            </div>

                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-7 h-7 shrink-0"
                              onClick={() => toggleBookmark(wordKey)}
                            >
                              <Bookmark className={cn("w-3.5 h-3.5", isBookmarked && "fill-primary text-primary")} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="px-4 py-4">
      {/* Search Bar */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder={t.learning.searchVocabulary}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 pr-9 h-10"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-full hover:bg-muted"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Search results indicator */}
      {searchQuery && (
        <p className="text-xs text-muted-foreground mb-3">
          {t.t(t.learning.foundResults, { count: filteredCount, query: searchQuery })}
        </p>
      )}

      {/* Tabs for vocabulary sections */}
      <div className="flex gap-1 mb-3 p-1 bg-muted/50 rounded-lg">
        <button
          type="button"
          onClick={() => setActiveTab("words")}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors",
            activeTab === "words" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <BookOpen className="w-3.5 h-3.5" />
          {t.learning.words}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("expressions")}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors",
            activeTab === "expressions" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <MessageSquareText className="w-3.5 h-3.5" />
          {t.learning.expressions}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("topical")}
          className={cn(
            "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors",
            activeTab === "topical" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <ListTree className="w-3.5 h-3.5" />
          {t.learning.topical}
        </button>
      </div>

      {/* Tab content */}
      <div className="flex flex-col gap-2">
        {activeTab === "words" && (
          <>
            {!searchQuery && (
              <p className="text-xs text-muted-foreground mb-2">
                {t.learning.wordsDescription}
              </p>
            )}
            {filteredVocabulary.words.length > 0 ? (
              filteredVocabulary.words.map((entry, i) => renderWordEntry(entry, i))
            ) : isStreaming ? (
              <div className="flex flex-col items-center gap-2 py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t.common.loading || "Loading..."}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{t.learning.noWordsMatch}</p>
            )}
          </>
        )}

        {activeTab === "expressions" && (
          <>
            {!searchQuery && (
              <p className="text-xs text-muted-foreground mb-2">
                {t.learning.expressionsDescription}
              </p>
            )}
            {filteredVocabulary.expressions.length > 0 ? (
              filteredVocabulary.expressions.map((entry, i) => renderExpressionEntry(entry, i))
            ) : isStreaming ? (
              <div className="flex flex-col items-center gap-2 py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t.common.loading || "Loading..."}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{t.learning.noExpressionsMatch}</p>
            )}
          </>
        )}

        {activeTab === "topical" && (
          <>
            {!searchQuery && (
              <p className="text-xs text-muted-foreground mb-2">
                {t.learning.topicalDescription}
              </p>
            )}
            {filteredVocabulary.topical.length > 0 ? (
              renderTopicalSection()
            ) : isStreaming ? (
              <div className="flex flex-col items-center gap-2 py-8">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t.common.loading || "Loading..."}</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">{t.learning.noTopicalMatch}</p>
            )}
          </>
        )}
      </div>

      {/* Building Blocks Section - collapsible common grammar words */}
      {vocabulary.buildingBlocks && vocabulary.buildingBlocks.length > 0 && (
        <div className="mt-3 border-t pt-3">
          {buildingBlocksHidden ? (
            // Collapsed state - just show a small link to expand
            <button
              type="button"
              onClick={showBuildingBlocks}
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Puzzle className="w-3 h-3" />
              <span>{t.learning.showBuildingBlocks} ({vocabulary.buildingBlocks.length})</span>
            </button>
          ) : (
            // Visible state - show collapsible section
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => setBuildingBlocksExpanded(!buildingBlocksExpanded)}
                className="w-full flex items-center justify-between p-1.5 rounded-md bg-muted/50 hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <Puzzle className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium">{t.learning.buildingBlocks}</span>
                  <Badge variant="secondary" className="text-[11px]">
                    {vocabulary.buildingBlocks.length}
                  </Badge>
                </div>
                {buildingBlocksExpanded ? (
                  <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </button>

              {buildingBlocksExpanded && (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-muted-foreground px-1">
                    {t.learning.buildingBlocksDescription}
                  </p>

                  <div className="flex flex-col gap-1.5">
                    {vocabulary.buildingBlocks.map((block, i) => renderWordEntry(block, i, "block"))}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markBuildingBlocksAsKnown}
                    className="w-full mt-1.5 text-[11px] text-muted-foreground hover:text-foreground h-7"
                  >
                    <EyeOff className="w-3 h-3 mr-1" />
                    {t.learning.hideByDefault}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
